import { useContext } from "react";
import { Button } from "./ui/button";
import { LocationConfigurationContext } from "@/providers/LocationConfigurationProvider";
import { useCartStore } from "@/store/CartStore";
import { menuRoute } from "@/App";
import { useSuspenseQuery } from "@tanstack/react-query";
import { allMenusQueryOptions } from "@/hooks/useMenu";
import { motion } from "framer-motion";
import { UpsaleList } from "./UpsaleList";
import { useUpsales } from "@/hooks/useUpsales";
import { useRebate } from "@/hooks/useRebate";

export function Upsales({ goToCheckout }: { goToCheckout?: () => void }) {
  const { locationId } = menuRoute.useParams();
  const { upsales, rebate } = useContext(LocationConfigurationContext);
  const { cart, price } = useCartStore();
  const menusQuery = useSuspenseQuery(allMenusQueryOptions(locationId));

  const { hasUpsale, upsaleProducts } = useUpsales(
    cart,
    menusQuery.data,
    upsales
  );
  const { finalPrice } = useRebate(cart, menusQuery.data, rebate, price);

  if (!hasUpsale || upsaleProducts.length === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ x: "75%", opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: "-75%", opacity: 0 }}
      transition={{
        ease: "easeInOut",
        duration: 0.3,
      }}
      className="fixed inset-0 z-50 grid grid-rows-[60px,auto] bg-background h-full overflow-hidden w-full"
    >
      <header className="flex flex-row gap-4 justify-between w-full pt-4 pb-2 border-b px-4 bg-background z-[2]">
        <h3 className="text-lg font-medium tracking-tighter text-foreground">
          Need anything else?
        </h3>

        <Button
          variant={"outline"}
          onClick={goToCheckout}
          className="ml-auto w-auto whitespace-nowrap"
        >
          No thanks, skip {"->"}
        </Button>
      </header>

      <div className="max-h-full overflow-y-auto flex-grow pb-32">
        <UpsaleList products={upsaleProducts} />
      </div>

      <footer className="fixed inset-x-0 bottom-0 bg-background p-2 border-t z-[2] max-md:w-full">
        <Button onClick={goToCheckout} className="w-full">
          Continue to checkout (
          {finalPrice.toLocaleString("da-DK", {
            style: "currency",
            currency: "DKK",
          })}
          )
        </Button>
      </footer>
    </motion.div>
  );
}
